import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { GlassCard, GlassButton, GlassInput } from '../../components/ui/GlassComponents';

const OurPeople = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamMembers, setTeamMembers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredMembers, setFilteredMembers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');

  useEffect(() => {
    fetchTeamMembers();
    // Departments list is only available to team admins; a non-admin call
    // will quietly fail and we just skip the filter UI.
    axios.get('/api/departments')
      .then(r => setDepartments(r.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);

  useEffect(() => {
    const q = searchTerm.toLowerCase();
    const filtered = teamMembers.filter(member => {
      const deptLabel = (member.departmentLabel || member.department || '').toLowerCase();
      const matchesSearch =
        `${member.firstName} ${member.lastName}`.toLowerCase().includes(q) ||
        member.email.toLowerCase().includes(q) ||
        (member.jobTitle && member.jobTitle.toLowerCase().includes(q)) ||
        deptLabel.includes(q);
      if (!matchesSearch) return false;
      if (departmentFilter === 'all') return true;
      if (departmentFilter === 'none') return !member.departmentId && !member.department;
      return member.departmentId === departmentFilter;
    });
    setFilteredMembers(filtered);
  }, [teamMembers, searchTerm, departmentFilter]);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/hr/team-members');
      setTeamMembers(response.data.teamMembers);
    } catch (error) {
      console.error('Error fetching team members:', error);
      toast.error('Failed to fetch team members');
    } finally {
      setLoading(false);
    }
  };

  const getAvatarUrl = (avatarPath) => {
    if (!avatarPath) return null;
    
    if (avatarPath.startsWith('http')) {
      return avatarPath;
    } else if (avatarPath.startsWith('/uploads/')) {
      return `${axios.defaults.baseURL}${avatarPath}`;
    } else if (avatarPath.includes('/uploads/')) {
      const uploadPath = avatarPath.substring(avatarPath.indexOf('/uploads/'));
      return `${axios.defaults.baseURL}${uploadPath}`;
    }
    
    return `${axios.defaults.baseURL}${avatarPath}`;
  };

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    
    const date = new Date(lastLogin);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffMinutes = Math.floor(diffTime / (1000 * 60));
    const diffHours = Math.floor(diffTime / (1000 * 60 * 60));
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    // More accurate time formatting
    if (diffMinutes < 5) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} minutes ago`;
    if (diffHours < 24) return `${diffHours} hours ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays <= 7) return `${diffDays} days ago`;
    if (diffDays <= 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const handleViewProfile = (memberId) => {
    navigate(`/dashboard/profile/${memberId}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-900 dark:via-blue-900 dark:to-purple-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
            Our People
          </h1>
          <p className="text-gray-600 dark:text-gray-300">
            Manage your team members and their information
          </p>
        </div>

        {/* Search and Stats */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-8">
          {/* Search */}
          <div className="lg:col-span-3">
            <GlassCard className="p-6">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <GlassInput
                  type="text"
                  placeholder="Search team members by name, email, job title, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 w-full"
                />
              </div>
              {departments.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-gray-600 dark:text-gray-300">Department:</span>
                  <button
                    type="button"
                    onClick={() => setDepartmentFilter('all')}
                    className={`px-3 py-1 rounded-full border ${departmentFilter === 'all' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white/50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}
                  >
                    All
                  </button>
                  {departments.map(d => (
                    <button
                      type="button"
                      key={d.id}
                      onClick={() => setDepartmentFilter(d.id)}
                      className={`px-3 py-1 rounded-full border ${departmentFilter === d.id ? 'bg-primary-600 text-white border-primary-600' : 'bg-white/50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}
                    >
                      {d.name}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setDepartmentFilter('none')}
                    className={`px-3 py-1 rounded-full border ${departmentFilter === 'none' ? 'bg-primary-600 text-white border-primary-600' : 'bg-white/50 dark:bg-gray-700/50 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200'}`}
                  >
                    Unassigned
                  </button>
                </div>
              )}
            </GlassCard>
          </div>

          {/* Team Stats */}
          <GlassCard className="p-6 text-center">
            <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {teamMembers.length}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Team Members
            </div>
          </GlassCard>
        </div>

        {/* Team Members Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {filteredMembers.map((member) => (
            <GlassCard key={member.id} className="p-6 hover:shadow-glow hover:scale-[1.02] transition-all duration-300 cursor-pointer"
              onClick={() => handleViewProfile(member.id)}>
              
              {/* Avatar and Basic Info */}
              <div className="text-center mb-4">
                <div className="relative mx-auto mb-4">
                  {member.avatar ? (
                    <img
                      src={getAvatarUrl(member.avatar)}
                      alt={`${member.firstName} ${member.lastName}`}
                      className="w-20 h-20 rounded-full object-cover border-4 border-white dark:border-gray-700 shadow-lg"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                      {member.firstName.charAt(0)}{member.lastName.charAt(0)}
                    </div>
                  )}
                  
                  {/* Online Status Indicator */}
                  <div className={`absolute bottom-1 right-1 w-4 h-4 rounded-full border-2 border-white dark:border-gray-700 ${
                    member.isActive ? 'bg-green-500' : 'bg-gray-400'
                  }`}></div>
                </div>

                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  {member.firstName} {member.lastName}
                </h3>
                
                {member.jobTitle && (
                  <p className="text-sm text-blue-600 dark:text-blue-400 font-medium mb-1">
                    {member.jobTitle}
                  </p>
                )}
                
                {(member.departmentLabel || member.department) && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {member.departmentLabel || member.department}
                  </p>
                )}
              </div>

              {/* Contact Info */}
              <div className="space-y-2 mb-4">
                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                  <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
                  </svg>
                  <span className="truncate">{member.email}</span>
                </div>
                
                {member.phone && (
                  <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                    <svg className="h-4 w-4 mr-2 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>{member.phone}</span>
                  </div>
                )}
              </div>

              {/* Last Login */}
              <div className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Last login: {formatLastLogin(member.lastLogin)}
              </div>

              {/* View Profile Button */}
              <div className="mt-4">
                <GlassButton
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewProfile(member.id);
                  }}
                  className="w-full text-sm py-2"
                >
                  View Profile
                </GlassButton>
              </div>
            </GlassCard>
          ))}
        </div>

        {/* No Results */}
        {filteredMembers.length === 0 && !loading && (
          <GlassCard className="p-12 text-center">
            <div className="text-gray-500 dark:text-gray-400">
              <svg className="h-12 w-12 mx-auto mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              <h3 className="text-lg font-medium mb-2">No team members found</h3>
              <p className="text-sm">
                {searchTerm ? 'Try adjusting your search terms.' : 'No team members available.'}
              </p>
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
};

export default OurPeople;
